import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import api from "../../../../api/axios";
import { COUNTRY_LIST } from "./countryList";

const TITLES = ["Mr", "Ms", "Mrs", "Miss", "Dr", "Prof"];
const GENDERS = ["Male", "Female", "Other"];
const ID_TYPES = [
  { value: "aadhar_card", label: "Aadhar Card" },
  { value: "passport", label: "Passport" },
  { value: "driving_license", label: "Driving License" },
  { value: "voter_id", label: "Voter ID" },
  { value: "pan_card", label: "Pan Card" },
  { value: "other", label: "Other" },
];

const UpdateGuest = ({ open, onClose, guestId, onUpdated }) => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  // FILE STATES
  const [guestImageFile, setGuestImageFile] = useState(null);
  const [frontDocFile, setFrontDocFile] = useState(null);
  const [backDocFile, setBackDocFile] = useState(null);

  // PREVIEWS
  const [imagePreview, setImagePreview] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);

  // Load guest data
  useEffect(() => {
    if (open && guestId) loadGuest();
  }, [open, guestId]);

  const loadGuest = async () => {
    try {
      const res = await api.get(`/pms/guests/${guestId}`, {
        params: { property_code: localStorage.getItem("property_code") },
      });

      const g = res.data;
      setForm(g);

      // Set preview URLs from backend model
      setImagePreview(g.guest_image_url);
      setFrontPreview(g.front_doc_url);
      setBackPreview(g.back_doc_url);
    } catch (err) {
      Swal.fire("Error", "Failed to load guest details", "error");
    }
  };

  // File handler
  const handleFileChange = (e, setter, previewSetter) => {
    const file = e.target.files[0];
    if (file) {
      setter(file);
      previewSetter(URL.createObjectURL(file));
    }
  };

  const updateGuest = async (e) => {
    e.preventDefault();

    const fd = new FormData();
    fd.append("_method", "PUT");
    fd.append("property_code", localStorage.getItem("property_code"));

    for (const [key, value] of Object.entries(form)) {
      fd.append(
        key,
        typeof value === "boolean" ? (value ? 1 : 0) : value ?? ""
      );
    }

    // Attach changed files only
    if (guestImageFile) fd.append("guest_image", guestImageFile);
    if (frontDocFile) fd.append("front_doc", frontDocFile);
    if (backDocFile) fd.append("back_doc", backDocFile);

    try {
      setLoading(true);

      await api.post(`/pms/guests/${guestId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      Swal.fire("Updated!", "Guest details updated successfully", "success");
      if (onUpdated) onUpdated();
      onClose();
    } catch (err) {
      Swal.fire("Update Failed", err.response?.data?.message || "", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="modal-backdrop fade show" />

      <div className="modal d-block" tabIndex={-1}>
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5>Edit Guest</h5>
              <button className="btn-close" onClick={onClose} />
            </div>

            <form onSubmit={updateGuest}>
              <div className="modal-body">
                <div className="row">
                  {/* Title */}
                  <div className="col-md-2 mb-2">
                    <label>Title</label>
                    <select
                      className="form-select"
                      value={form.title || ""}
                      onChange={(e) =>
                        setForm({ ...form, title: e.target.value })
                      }
                    >
                      <option value="">Select</option>
                      {TITLES.map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  {/* Full Name */}
                  <div className="col-md-5 mb-2">
                    <label>Full Name</label>
                    <input
                      className="form-control"
                      value={form.name || ""}
                      onChange={(e) =>
                        setForm({ ...form, name: e.target.value })
                      }
                    />
                  </div>

                  {/* Father Name */}
                  <div className="col-md-5 mb-2">
                    <label>Father Name</label>
                    <input
                      className="form-control"
                      value={form.father_name || ""}
                      onChange={(e) =>
                        setForm({ ...form, father_name: e.target.value })
                      }
                    />
                  </div>

                  {/* Gender */}
                  <div className="col-md-3 mb-2">
                    <label>Gender</label>
                    <select
                      className="form-select"
                      value={form.gender || ""}
                      onChange={(e) =>
                        setForm({ ...form, gender: e.target.value })
                      }
                    >
                      <option value="">Select</option>
                      {GENDERS.map((g) => (
                        <option key={g}>{g}</option>
                      ))}
                    </select>
                  </div>

                  {/* DOB */}
                  <div className="col-md-3 mb-2">
                    <label>DOB</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.dob || ""}
                      onChange={(e) =>
                        setForm({ ...form, dob: e.target.value })
                      }
                    />
                  </div>

                  {/* Anniversary */}
                  <div className="col-md-3 mb-2">
                    <label>Anniversary</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.anniversary || ""}
                      onChange={(e) =>
                        setForm({ ...form, anniversary: e.target.value })
                      }
                    />
                  </div>

                  {/* Nationality */}
                  <div className="col-md-3 mb-2">
                    <label>Nationality</label>
                    <input
                      className="form-control"
                      value={form.nationality || ""}
                      onChange={(e) =>
                        setForm({ ...form, nationality: e.target.value })
                      }
                    />
                  </div>

                  {/* Mobile + Country Code */}
                  <div className="col-md-6 mb-2">
                    <label>Mobile No</label>
                    <div className="input-group">
                      <select
                        className="form-select"
                        style={{ maxWidth: "160px" }}
                        value={form.country_code || ""}
                        onChange={(e) =>
                          setForm({ ...form, country_code: e.target.value })
                        }
                      >
                        <option value="">Code</option>
                        {COUNTRY_LIST.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.name} ({c.code})
                          </option>
                        ))}
                      </select>

                      <input
                        className="form-control"
                        value={form.mobile_no || ""}
                        onChange={(e) =>
                          setForm({ ...form, mobile_no: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="col-md-6 mb-2">
                    <label>Email</label>
                    <input
                      className="form-control"
                      type="email"
                      value={form.email || ""}
                      onChange={(e) =>
                        setForm({ ...form, email: e.target.value })
                      }
                    />
                  </div>

                  {/* Country */}
                  <div className="col-md-4 mb-2">
                    <label>Country</label>
                    <input
                      className="form-control"
                      value={form.country || ""}
                      onChange={(e) =>
                        setForm({ ...form, country: e.target.value })
                      }
                    />
                  </div>

                  {/* State */}
                  <div className="col-md-4 mb-2">
                    <label>State</label>
                    <input
                      className="form-control"
                      value={form.state || ""}
                      onChange={(e) =>
                        setForm({ ...form, state: e.target.value })
                      }
                    />
                  </div>

                  {/* City */}
                  <div className="col-md-4 mb-2">
                    <label>City</label>
                    <input
                      className="form-control"
                      value={form.city || ""}
                      onChange={(e) =>
                        setForm({ ...form, city: e.target.value })
                      }
                    />
                  </div>

                  {/* Zipcode */}
                  <div className="col-md-3 mb-2">
                    <label>Zipcode</label>
                    <input
                      className="form-control"
                      value={form.zipcode || ""}
                      onChange={(e) =>
                        setForm({ ...form, zipcode: e.target.value })
                      }
                    />
                  </div>

                  {/* Address */}
                  <div className="col-md-12 mb-2">
                    <label>Address</label>
                    <textarea
                      className="form-control"
                      value={form.address || ""}
                      onChange={(e) =>
                        setForm({ ...form, address: e.target.value })
                      }
                    />
                  </div>

                  {/* Identity Type */}
                  <div className="col-md-6 mb-2">
                    <label>Identity Type</label>
                    <select
                      className="form-select"
                      value={form.identity_type || ""}
                      onChange={(e) =>
                        setForm({ ...form, identity_type: e.target.value })
                      }
                    >
                      {ID_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Identity Number */}
                  <div className="col-md-6 mb-2">
                    <label>Identity Number</label>
                    <input
                      className="form-control"
                      value={form.identity_no || ""}
                      onChange={(e) =>
                        setForm({ ...form, identity_no: e.target.value })
                      }
                    />
                  </div>

                  {/* Identity Comments */}
                  <div className="col-md-12 mb-2">
                    <label>Identity Comments</label>
                    <textarea
                      className="form-control"
                      value={form.identity_comments || ""}
                      onChange={(e) =>
                        setForm({ ...form, identity_comments: e.target.value })
                      }
                    />
                  </div>

                  {/* VIP + Blacklisted */}
                  <div className="col-12 d-flex gap-5 mb-3">
                    <label>
                      <input
                        type="checkbox"
                        checked={form.is_vip || false}
                        onChange={() =>
                          setForm({ ...form, is_vip: !form.is_vip })
                        }
                      />{" "}
                      VIP
                    </label>

                    <label>
                      <input
                        type="checkbox"
                        checked={form.is_blacklisted || false}
                        onChange={() =>
                          setForm({
                            ...form,
                            is_blacklisted: !form.is_blacklisted,
                          })
                        }
                      />{" "}
                      Blacklisted
                    </label>
                  </div>

                  {/* FILE UPLOAD + PREVIEWS */}
                  <div className="row mt-3">
                    {/* Guest Image */}
                    <div className="col-md-4 mb-3">
                      <label>Guest Image</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={(e) =>
                          handleFileChange(
                            e,
                            setGuestImageFile,
                            setImagePreview
                          )
                        }
                      />
                      {imagePreview && (
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="img-thumbnail mt-2"
                          style={{ width: 150, borderRadius: 8 }}
                        />
                      )}
                    </div>

                    {/* Front Doc */}
                    <div className="col-md-4 mb-3">
                      <label>Front Document</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={(e) =>
                          handleFileChange(e, setFrontDocFile, setFrontPreview)
                        }
                      />
                      {frontPreview && (
                        <a
                          href={frontPreview}
                          className="d-block mt-2"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Preview Front
                        </a>
                      )}
                    </div>

                    {/* Back Doc */}
                    <div className="col-md-4 mb-3">
                      <label>Back Document</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={(e) =>
                          handleFileChange(e, setBackDocFile, setBackPreview)
                        }
                      />
                      {backPreview && (
                        <a
                          href={backPreview}
                          className="d-block mt-2"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Preview Back
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={onClose}>
                  Close
                </button>
                <button className="btn btn-primary" disabled={loading}>
                  {loading ? "Updating..." : "Update Guest"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpdateGuest;
